import React from "react";
import {Link} from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Divider,
    Group,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
    useMantineTheme
} from "@mantine/core";
import {useConfig} from "../ConfigContext";
import VentureCard from "../components/venture/VentureCard";
import StudioBackdrop from "../components/StudioBackdrop";

function Home() {
    const theme = useMantineTheme();
    const {home, ventures} = useConfig();

    const studioNarrative = home.body.split("|").map((paragraph, index) =>
        paragraph.trimStart()[0] === "#" ? (
            <Text key={index} fw={800} mt="lg" size="lg">
                {paragraph.trim().replace(/^#/, '')}
            </Text>
        ) : (
            <Text key={index} mt="xs">
                {paragraph.trim()}
            </Text>
        )
    );

    return (
        <Container size="xl" py="xl">
            <Stack gap={72}>
                {/* Hero */}
                <Box style={{position: 'relative'}}>
                    <StudioBackdrop/>
                    <Stack gap="md" style={{position: 'relative', zIndex: 1}}>
                    <Text fw={700} c={theme.colors.accent[2]} tt="uppercase" size="sm">
                        {home.eyebrow}
                    </Text>
                    <Title style={{fontSize: 'clamp(32px, 5vw, 56px)', maxWidth: '18ch'}}>
                        {home.title}
                    </Title>
                    <Text size="lg" maw={760}>{home.subtitle}</Text>
                    <Group mt="md">
                        <Button component={Link} to={home.primaryCta.link} radius="xl" size="md" color="brand.6">
                            {home.primaryCta.label}
                        </Button>
                        <Button
                            component={Link}
                            to={home.secondaryCta.link}
                            radius="xl"
                            size="md"
                            variant="outline"
                            color="brand"
                        >
                            {home.secondaryCta.label}
                        </Button>
                    </Group>
                    </Stack>
                </Box>

                {/* The portfolio */}
                <Stack gap="lg">
                    <Stack gap="sm">
                        <Title order={2} c={theme.colors.brand[6]}>{home.venturesHeading}</Title>
                        <Text maw={760}>{home.venturesIntro}</Text>
                    </Stack>
                    <SimpleGrid cols={{base: 1, sm: 2, lg: 4}} spacing="lg">
                        {ventures.map((venture) => (
                            <VentureCard key={venture.slug} venture={venture}/>
                        ))}
                    </SimpleGrid>
                </Stack>

                <Divider/>

                {/* How we build — the studio method */}
                <Stack gap="lg">
                    <Stack gap="sm">
                        <Title order={2} c={theme.colors.brand[6]}>{home.method.heading}</Title>
                        <Text maw={760}>{home.method.intro}</Text>
                    </Stack>
                    <SimpleGrid cols={{base: 1, sm: 2, lg: 4}} spacing="lg">
                        {home.method.steps.map((step, index) => (
                            <Paper key={step.title} withBorder radius="lg" p="lg" shadow="sm" h="100%">
                                <Stack gap="sm">
                                    <ThemeIcon variant="filled" color="brand.6" size={44} radius="xl">
                                        <Text fw={700} c={theme.colors.gray[0]}>{index + 1}</Text>
                                    </ThemeIcon>
                                    <Text fw={700} size="lg">{step.title}</Text>
                                    <Text size="sm" c="dimmed">{step.description}</Text>
                                </Stack>
                            </Paper>
                        ))}
                    </SimpleGrid>
                </Stack>

                {/* Shared platform */}
                <Stack gap="lg">
                    <Title order={2} c={theme.colors.brand[6]}>What every venture inherits</Title>
                    <SimpleGrid cols={{base: 1, sm: 2, lg: 4}} spacing="xl">
                        {home.pillars.map((pillar) => (
                            <Stack key={pillar.title} gap="xs">
                                {pillar.icon && (
                                    <ThemeIcon variant="light" color="brand" size={52} radius="xl">
                                        {React.createElement(pillar.icon, {
                                            size: 28,
                                            stroke: 1.5,
                                            color: theme.colors.brand[6]
                                        })}
                                    </ThemeIcon>
                                )}
                                <Text fw={700} size="lg">{pillar.title}</Text>
                                <Text size="sm" c="dimmed">{pillar.description}</Text>
                            </Stack>
                        ))}
                    </SimpleGrid>
                </Stack>

                <Divider/>

                {/* Studio thesis */}
                <Stack gap={0} maw={860}>
                    {studioNarrative}
                    <Group mt="xl">
                        <Button component={Link} to="/ventures" radius="xl" variant="light" color="brand">
                            See the portfolio
                        </Button>
                        <Button component={Link} to="/contact-us" radius="xl" color="brand.6">
                            Talk to us
                        </Button>
                    </Group>
                </Stack>
            </Stack>
        </Container>
    );
}

export default Home;
